import { useEffect, useMemo, useRef } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert as MuiAlert } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { getVesselCallStatusByVcn } from "@services/vesselCallApi";
import { t } from "i18next";
import { FormProvider, useFormState } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { hasActiveHazardCargo, syncTruckEntryDateField } from "./truckEntryDateSync";
import type { TruckGatepassOutletContext } from "./types";

const TRANSLATION_KEY = "gatepass";
const VESSEL_CALL_SERVICE = "bff-gatepass";
const ENABLE_DESTINATION_VCN_LOOKUP = false;

type GenericReferenceItem = Record<string, unknown>;

const toNormalizedString = (value: unknown): string => String(value ?? "").trim();

const resolveVesselNameFromVcnResponse = (response: unknown): string => {
  const typed = response as { vesselName?: unknown };
  return toNormalizedString(typed?.vesselName ?? "");
};

const TRUCK_JOB_IDENTIFIER_FIELDS = ["destinationVcn", "serviceNumber", "jobNumber"] as const;
const PORT_JOB_FIELDS = ["jobNumber", "jobDescription", "deliverTo"] as const;
const HAZARD_OPTIONAL_JOB_FIELDS = [...PORT_JOB_FIELDS, "jobStartDate", "jobEndDate"] as const;
const VESSEL_JOB_FIELDS = ["destinationVcn", "serviceNumber"] as const;
const JOB_MODE_DEPENDENT_FIELDS = [
  ...TRUCK_JOB_IDENTIFIER_FIELDS,
  "vesselName",
  ...PORT_JOB_FIELDS,
] as const;

const JobDetails = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const { submitCount, isSubmitted } = useFormState({ control: formData.control });
  const legacyMigrationDoneRef = useRef(false);

  const { options: companiesRaw } = useFormattedReference<GenericReferenceItem>(
    REFERENCE_DATA_TYPES.COMPANIES,
    "bff-gatepass",
  );

  const companyOptions = useMemo(
    () =>
      companiesRaw
        .map((item) => {
          const value = item.companyCode;
          const label = item.companyNameEnglish;
          return value && label ? { value, label } : null;
        })
        .filter((item): item is { value: string; label: string } => Boolean(item)),
    [companiesRaw],
  );

  const [
    truckType = "TRUCK",
    cargoDestination = "",
    destinationVcn = "",
    vesselName = "",
    serviceNumber = "",
    jobNumber = "",
    jobDescription = "",
    deliverTo = "",
    cargos = [],
  ] = formData.watch([
    "truckType",
    "cargoDestination",
    "destinationVcn",
    "vesselName",
    "serviceNumber",
    "jobNumber",
    "jobDescription",
    "deliverTo",
    "cargos",
  ]) as [
    TruckGatepassFormData["truckType"],
    TruckGatepassFormData["cargoDestination"],
    TruckGatepassFormData["destinationVcn"],
    TruckGatepassFormData["vesselName"],
    TruckGatepassFormData["serviceNumber"],
    TruckGatepassFormData["jobNumber"],
    TruckGatepassFormData["jobDescription"],
    TruckGatepassFormData["deliverTo"],
    TruckGatepassFormData["cargos"],
  ];
  const hasHazardCargo = hasActiveHazardCargo(cargos);

  const isTruckMode = String(truckType).toUpperCase() === "TRUCK";
  const isCargoMode = String(truckType).toUpperCase() === "CARGO";
  const isVesselCargoDestination = toNormalizedString(cargoDestination).toUpperCase() === "VESSEL";
  const isPortCargoDestination = toNormalizedString(cargoDestination).toUpperCase() === "PORT";
  const hasCargoDestinationJobRules = isVesselCargoDestination || isPortCargoDestination;
  const hasDestinationVcn = Boolean(toNormalizedString(destinationVcn));
  const hasVesselName = Boolean(toNormalizedString(vesselName));
  const hasServiceNumber = Boolean(toNormalizedString(serviceNumber));
  const hasJobNumber = Boolean(toNormalizedString(jobNumber));
  const hasJobDescription = Boolean(toNormalizedString(jobDescription));
  const hasDeliverTo = Boolean(toNormalizedString(deliverTo));
  const hasTruckJobIdentifier = hasDestinationVcn || hasServiceNumber || hasJobNumber;
  const hasRequiredTruckJobIdentifier = hasHazardCargo
    ? hasDestinationVcn || hasServiceNumber
    : hasTruckJobIdentifier;
  const truckJobDetailsRequiredMessage = t(`${TRANSLATION_KEY}.truck-job-details-required`);
  const vesselDestinationRequiredMessage = t(
    `${TRANSLATION_KEY}.vessel-destination-vcn-or-service-required`,
  );
  const portJobFieldsRequiredMessage = t(`${TRANSLATION_KEY}.port-destination-job-fields-required`);
  const truckJobIdentifierFieldLabels = useMemo(
    () => ({
      destinationVcn: t(`${TRANSLATION_KEY}.destination-vcn`),
      serviceNumber: t(`${TRANSLATION_KEY}.service-number`),
      jobNumber: t(`${TRANSLATION_KEY}.job-number`),
      jobDescription: t(`${TRANSLATION_KEY}.job-description`),
      deliverTo: t(`${TRANSLATION_KEY}.deliver-to`),
    }),
    [],
  );
  const hasAttemptedSubmit = submitCount > 0 || isSubmitted;
  const showTruckJobIdentifierRequired =
    isTruckMode &&
    hasAttemptedSubmit &&
    !hasRequiredTruckJobIdentifier &&
    !hasCargoDestinationJobRules;
  const showVesselDestinationRequired =
    isVesselCargoDestination && hasAttemptedSubmit && !hasDestinationVcn && !hasServiceNumber;
  const showPortJobValidation = isPortCargoDestination && !hasHazardCargo && hasAttemptedSubmit;
  const showPortJobFieldsRequired = showPortJobValidation && (!hasJobNumber || !hasJobDescription);
  const isDestinationVcnRequired = showTruckJobIdentifierRequired || showVesselDestinationRequired;
  const isServiceNumberRequired = showTruckJobIdentifierRequired || showVesselDestinationRequired;
  const isJobNumberRequired =
    isTruckMode ||
    (!hasHazardCargo &&
      (showTruckJobIdentifierRequired || (showPortJobValidation && !hasJobNumber)));
  const isJobDescriptionRequired = isTruckMode || (showPortJobValidation && !hasJobDescription);
  const isJobStartDateRequired = isTruckMode;
  const isJobEndDateRequired = isTruckMode;
  const showVesselNameRequired = hasDestinationVcn && hasAttemptedSubmit && !hasVesselName;

  const { clearErrors, setError, trigger } = formData;

  const requiredFieldMessage = (label: string) => t("form-validation.required", { label });

  const applyModeDependentJobErrors = () => {
    if (showTruckJobIdentifierRequired) {
      const requiredFields = hasHazardCargo ? VESSEL_JOB_FIELDS : TRUCK_JOB_IDENTIFIER_FIELDS;

      requiredFields.forEach((field) => {
        setError(field, {
          type: "required",
          message: requiredFieldMessage(truckJobIdentifierFieldLabels[field]),
        });
      });
    }

    if (showVesselDestinationRequired) {
      VESSEL_JOB_FIELDS.forEach((field) => {
        setError(field, {
          type: "required",
          message: requiredFieldMessage(truckJobIdentifierFieldLabels[field]),
        });
      });
    }

    if (showPortJobFieldsRequired) {
      if (!hasJobNumber) {
        setError("jobNumber", {
          type: "required",
          message: requiredFieldMessage(truckJobIdentifierFieldLabels.jobNumber),
        });
      }

      if (!hasJobDescription) {
        setError("jobDescription", {
          type: "required",
          message: requiredFieldMessage(truckJobIdentifierFieldLabels.jobDescription),
        });
      }
    }

    if (isCargoMode && isPortCargoDestination && !hasDeliverTo) {
      setError("deliverTo", {
        type: "required",
        message: requiredFieldMessage(truckJobIdentifierFieldLabels.deliverTo),
      });
    }

    if (showVesselNameRequired) {
      setError("vesselName", {
        type: "required",
        message: requiredFieldMessage(t(`${TRANSLATION_KEY}.vessel-name`)),
      });
    }
  };

  const prevModeRef = useRef({
    truckType: toNormalizedString(truckType).toUpperCase(),
    cargoDestination: toNormalizedString(cargoDestination).toUpperCase(),
  });

  useEffect(() => {
    const nextTruckType = toNormalizedString(truckType).toUpperCase();
    const nextCargoDestination = toNormalizedString(cargoDestination).toUpperCase();
    const previousMode = prevModeRef.current;

    if (
      previousMode.truckType === nextTruckType &&
      previousMode.cargoDestination === nextCargoDestination
    ) {
      return;
    }

    prevModeRef.current = {
      truckType: nextTruckType,
      cargoDestination: nextCargoDestination,
    };

    clearErrors(JOB_MODE_DEPENDENT_FIELDS);

    if (hasAttemptedSubmit) {
      applyModeDependentJobErrors();
      void trigger(JOB_MODE_DEPENDENT_FIELDS);
    }
  }, [
    truckType,
    cargoDestination,
    clearErrors,
    hasAttemptedSubmit,
    trigger,
    hasHazardCargo,
    hasJobDescription,
    hasJobNumber,
    hasDestinationVcn,
    hasVesselName,
    setError,
    showPortJobFieldsRequired,
    showTruckJobIdentifierRequired,
    showVesselDestinationRequired,
    showVesselNameRequired,
    truckJobIdentifierFieldLabels,
  ]);

  const handleTruckJobIdentifierChange = (
    key: (typeof TRUCK_JOB_IDENTIFIER_FIELDS)[number],
    value: unknown,
  ) => {
    updateFormData(key, value);

    if (!hasAttemptedSubmit) return;

    const hasValue = Boolean(toNormalizedString(value));
    if (!hasValue) return;

    if (isVesselCargoDestination) {
      if (key === "destinationVcn" || key === "serviceNumber") {
        clearErrors(VESSEL_JOB_FIELDS);
      }
      return;
    }

    if (isPortCargoDestination) {
      return;
    }

    if (!isTruckMode) return;

    clearErrors(TRUCK_JOB_IDENTIFIER_FIELDS);
    clearErrors(VESSEL_JOB_FIELDS);
  };

  const handlePortJobFieldChange = (key: (typeof PORT_JOB_FIELDS)[number], value: unknown) => {
    updateFormData(key, value);

    if (!hasAttemptedSubmit || !isPortCargoDestination || hasHazardCargo) return;

    const nextJobNumber = key === "jobNumber" ? value : jobNumber;
    const nextJobDescription = key === "jobDescription" ? value : jobDescription;
    const hasBothPortJobFields =
      Boolean(toNormalizedString(nextJobNumber)) && Boolean(toNormalizedString(nextJobDescription));

    if (hasBothPortJobFields) {
      clearErrors(PORT_JOB_FIELDS);
      return;
    }

    if (toNormalizedString(nextJobNumber)) {
      clearErrors("jobNumber");
    }

    if (toNormalizedString(nextJobDescription)) {
      clearErrors("jobDescription");
    }
  };

  const validateDestinationVcn = async () => {
    if (!ENABLE_DESTINATION_VCN_LOOKUP) return;

    const vcn = toNormalizedString(formData.getValues("destinationVcn"));
    if (!vcn) {
      formData.setValue("vesselName", "", { shouldDirty: true, shouldValidate: false });
      if (hasHazardCargo) {
        formData.setError("destinationVcn", {
          type: "required",
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.destination-vcn`),
          }),
        });
      } else {
        formData.clearErrors("destinationVcn");
      }
      updateFormData("vesselName", "");
      return;
    }

    try {
      const response = await getVesselCallStatusByVcn(VESSEL_CALL_SERVICE, vcn);
      const found = response?.isApproved === true;

      if (!found) {
        formData.setValue("vesselName", "", { shouldDirty: true, shouldValidate: false });
        formData.setError("destinationVcn", {
          type: "validate",
          message: t(`${TRANSLATION_KEY}.valid-vcn-error`),
        });
      } else {
        const vesselName = resolveVesselNameFromVcnResponse(response);
        formData.setValue("vesselName", vesselName, { shouldDirty: true, shouldValidate: true });
        formData.clearErrors("destinationVcn");
        updateFormData("vesselName", vesselName);
      }
    } catch {
      formData.setValue("vesselName", "", { shouldDirty: true, shouldValidate: false });
      formData.setError("destinationVcn", {
        type: "validate",
        message: t(`${TRANSLATION_KEY}.valid-vcn-error`),
      });
    }
  };

  useEffect(() => {
    if (isTruckMode) return;

    clearErrors(TRUCK_JOB_IDENTIFIER_FIELDS);
  }, [clearErrors, isTruckMode]);

  useEffect(() => {
    if (!hasAttemptedSubmit) return;

    if (isVesselCargoDestination) {
      clearErrors(["jobNumber"]);
      return;
    }

    if (isPortCargoDestination) {
      clearErrors(["destinationVcn", "serviceNumber"]);
    }
  }, [clearErrors, hasAttemptedSubmit, isPortCargoDestination, isVesselCargoDestination]);

  useEffect(() => {
    if (isPortCargoDestination) return;

    clearErrors(PORT_JOB_FIELDS);
  }, [clearErrors, isPortCargoDestination]);

  useEffect(() => {
    if (isCargoMode && isPortCargoDestination) return;

    if (!hasDeliverTo) return;

    formData.setValue("deliverTo", "", {
      shouldDirty: true,
      shouldValidate: false,
    });
    clearErrors("deliverTo");
    updateFormData("deliverTo", "");
  }, [formData, hasDeliverTo, isCargoMode, isPortCargoDestination, updateFormData]);

  useEffect(() => {
    if (isVesselCargoDestination) return;

    if (!showTruckJobIdentifierRequired) {
      clearErrors(VESSEL_JOB_FIELDS);
    }
  }, [clearErrors, isVesselCargoDestination, showTruckJobIdentifierRequired]);

  useEffect(() => {
    if (!hasHazardCargo) return;

    clearErrors(HAZARD_OPTIONAL_JOB_FIELDS);
  }, [clearErrors, hasHazardCargo]);

  useEffect(() => {
    if (!showTruckJobIdentifierRequired) return;

    const requiredFields = hasHazardCargo ? VESSEL_JOB_FIELDS : TRUCK_JOB_IDENTIFIER_FIELDS;

    requiredFields.forEach((field) => {
      setError(field, {
        type: "required",
        message: requiredFieldMessage(truckJobIdentifierFieldLabels[field]),
      });
    });
  }, [hasHazardCargo, setError, showTruckJobIdentifierRequired, truckJobIdentifierFieldLabels]);

  useEffect(() => {
    if (!showVesselDestinationRequired) return;

    VESSEL_JOB_FIELDS.forEach((field) => {
      setError(field, {
        type: "required",
        message: requiredFieldMessage(truckJobIdentifierFieldLabels[field]),
      });
    });
  }, [setError, showVesselDestinationRequired, truckJobIdentifierFieldLabels]);

  useEffect(() => {
    if (!showPortJobFieldsRequired) return;

    if (!hasJobNumber) {
      setError("jobNumber", {
        type: "required",
        message: requiredFieldMessage(truckJobIdentifierFieldLabels.jobNumber),
      });
    }

    if (!hasJobDescription) {
      setError("jobDescription", {
        type: "required",
        message: requiredFieldMessage(truckJobIdentifierFieldLabels.jobDescription),
      });
    }
  }, [
    hasJobDescription,
    hasJobNumber,
    setError,
    showPortJobFieldsRequired,
    truckJobIdentifierFieldLabels,
  ]);

  useEffect(() => {
    if (!hasDestinationVcn) {
      clearErrors("vesselName");
      return;
    }

    if (!showVesselNameRequired) return;

    setError("vesselName", {
      type: "required",
      message: requiredFieldMessage(t(`${TRANSLATION_KEY}.vessel-name`)),
    });
  }, [clearErrors, hasDestinationVcn, setError, showVesselNameRequired]);

  useEffect(() => {
    if (!hasVesselName) return;

    clearErrors("vesselName");
  }, [clearErrors, hasVesselName]);

  useEffect(() => {
    if (!hasAttemptedSubmit) return;

    if (isVesselCargoDestination) {
      if (hasDestinationVcn || hasServiceNumber) {
        clearErrors(VESSEL_JOB_FIELDS);
      }
      return;
    }

    if (isPortCargoDestination) {
      if (hasJobNumber) {
        clearErrors("jobNumber");
      }
      if (hasJobDescription) {
        clearErrors("jobDescription");
      }
      return;
    }

    if (!isTruckMode) return;

    if (hasHazardCargo) {
      if (hasDestinationVcn || hasServiceNumber) {
        clearErrors(VESSEL_JOB_FIELDS);
      }
      return;
    }

    if (hasTruckJobIdentifier) {
      clearErrors(TRUCK_JOB_IDENTIFIER_FIELDS);
    }

    if (hasDestinationVcn || hasServiceNumber) {
      clearErrors(VESSEL_JOB_FIELDS);
    }
  }, [
    clearErrors,
    hasAttemptedSubmit,
    hasDestinationVcn,
    hasJobDescription,
    hasJobNumber,
    hasDeliverTo,
    hasHazardCargo,
    hasServiceNumber,
    hasTruckJobIdentifier,
    isCargoMode,
    isPortCargoDestination,
    isTruckMode,
    isVesselCargoDestination,
  ]);

  useEffect(() => {
    if (legacyMigrationDoneRef.current) return;

    const firstCargo = cargos[0] as Record<string, unknown> | undefined;
    if (!firstCargo) return;

    const legacyFields: Array<keyof TruckGatepassFormData> = [
      "destinationVcn",
      "vesselName",
      "serviceNumber",
      "cargoHandlingParty",
      "jobNumber",
      "jobDescription",
      "deliverTo",
      "jobStartDate",
      "jobEndDate",
    ];

    let migrated = false;
    legacyFields.forEach((field) => {
      const currentValue = formData.getValues(field);
      const hasCurrentValue =
        currentValue !== undefined && currentValue !== null && String(currentValue).trim() !== "";
      if (hasCurrentValue) return;

      const legacyValue = firstCargo[field as string];
      if (legacyValue === undefined || legacyValue === null || String(legacyValue).trim() === "") {
        return;
      }

      formData.setValue(field, legacyValue as never, {
        shouldDirty: false,
        shouldValidate: false,
      });
      updateFormData(field, legacyValue);
      migrated = true;
    });

    if (migrated) {
      legacyMigrationDoneRef.current = true;
    }
  }, [cargos, formData, updateFormData]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${TRANSLATION_KEY}.job-details`)}>
        {showTruckJobIdentifierRequired && (
          <div className="col-span-2 mb-4">
            <MuiAlert
              severity="info"
              icon={<InfoOutlinedIcon color="info" fontSize="inherit" />}
              data-testid="truck-job-identifier-guidance"
            >
              {truckJobDetailsRequiredMessage}
            </MuiAlert>
          </div>
        )}

        {showVesselDestinationRequired && (
          <div className="col-span-2 mb-4">
            <MuiAlert
              severity="info"
              icon={<InfoOutlinedIcon color="info" fontSize="inherit" />}
              data-testid="vessel-cargo-destination-guidance"
            >
              {vesselDestinationRequiredMessage}
            </MuiAlert>
          </div>
        )}

        {showPortJobFieldsRequired && (
          <div className="col-span-2 mb-4">
            <MuiAlert
              severity="info"
              icon={<InfoOutlinedIcon color="info" fontSize="inherit" />}
              data-testid="port-cargo-destination-guidance"
            >
              {portJobFieldsRequiredMessage}
            </MuiAlert>
          </div>
        )}

        {isCargoMode && isPortCargoDestination && (
          <div className="col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormItem<TruckGatepassFormData, "deliverTo">
              required
              name="deliverTo"
              type="text"
              maxChars={100}
              label={t(`${TRANSLATION_KEY}.deliver-to`)}
              testId="truck-job-deliver-to"
              onFieldChange={(key, value) =>
                handlePortJobFieldChange(key as (typeof PORT_JOB_FIELDS)[number], value)
              }
            />
            <div className="hidden md:block" />
          </div>
        )}

        <FormItem<TruckGatepassFormData, "destinationVcn">
          required={isDestinationVcnRequired}
          name="destinationVcn"
          type="text"
          label={t(`${TRANSLATION_KEY}.destination-vcn`)}
          testId="truck-job-destination-vcn"
          slotProps={{
            htmlInput: {
              onBlur: () => {
                void validateDestinationVcn();
              },
            },
          }}
          onFieldChange={handleTruckJobIdentifierChange}
        />

        <FormItem<TruckGatepassFormData, "vesselName">
          required={hasDestinationVcn}
          name="vesselName"
          type="text"
          label={t(`${TRANSLATION_KEY}.vessel-name`)}
          testId="truck-job-vessel-name"
          onFieldChange={(key, value) => {
            updateFormData(key, value);
            if (toNormalizedString(value)) {
              clearErrors("vesselName");
            }
          }}
        />

        <FormItem<TruckGatepassFormData, "serviceNumber">
          required={isServiceNumberRequired}
          name="serviceNumber"
          type="number"
          label={t(`${TRANSLATION_KEY}.service-number`)}
          decimals={0}
          testId="truck-job-service-number"
          onFieldChange={handleTruckJobIdentifierChange}
        />

        <FormItem<TruckGatepassFormData, "cargoHandlingParty">
          name="cargoHandlingParty"
          type="autocomplete"
          label={t(`${TRANSLATION_KEY}.cargo-handling-party`)}
          options={companyOptions}
          testId="truck-job-cargo-handling-party"
          onFieldChange={updateFormData}
        />

        <FormItem<TruckGatepassFormData, "jobNumber">
          required={isJobNumberRequired}
          name="jobNumber"
          type="number"
          label={t(`${TRANSLATION_KEY}.job-number`)}
          decimals={0}
          testId="truck-job-number"
          onFieldChange={(key, value) => {
            if (isPortCargoDestination && !hasHazardCargo) {
              handlePortJobFieldChange(key as (typeof PORT_JOB_FIELDS)[number], value);
              return;
            }
            handleTruckJobIdentifierChange(
              key as (typeof TRUCK_JOB_IDENTIFIER_FIELDS)[number],
              value,
            );
          }}
        />

        <FormItem<TruckGatepassFormData, "jobDescription">
          required={isJobDescriptionRequired}
          name="jobDescription"
          type="text"
          maxChars={500}
          label={t(`${TRANSLATION_KEY}.job-description`)}
          testId="truck-job-description"
          onFieldChange={(key, value) =>
            handlePortJobFieldChange(key as (typeof PORT_JOB_FIELDS)[number], value)
          }
        />

        <FormItem<TruckGatepassFormData, "jobStartDate">
          required={isJobStartDateRequired}
          name="jobStartDate"
          type="date-picker"
          label={t(`${TRANSLATION_KEY}.job-start-date`)}
          dateConstraint="not-past"
          timePicker={false}
          testId="truck-job-start-date"
          onFieldChange={(key, value) => {
            const normalizedValue = value == null || value === "" ? "" : value;
            if (normalizedValue === "") {
              formData.setValue(key, "" as never, { shouldDirty: true, shouldValidate: true });
              clearErrors(key);
              updateFormData(key, normalizedValue);
              return;
            }
            updateFormData(key, normalizedValue);
            syncTruckEntryDateField(formData, updateFormData, "jobStartDate", normalizedValue);
          }}
        />

        <FormItem<TruckGatepassFormData, "jobEndDate">
          required={isJobEndDateRequired}
          name="jobEndDate"
          type="date-picker"
          label={t(`${TRANSLATION_KEY}.job-end-date`)}
          dateConstraint="not-past"
          timePicker={false}
          testId="truck-job-end-date"
          onFieldChange={(key, value) => {
            const normalizedValue = value == null || value === "" ? "" : value;
            if (normalizedValue === "") {
              formData.setValue(key, "" as never, { shouldDirty: true, shouldValidate: true });
              clearErrors(key);
            }
            updateFormData(key, normalizedValue);
          }}
        />
      </FormSection>
    </FormProvider>
  );
};

export default JobDetails;
